// Inspecciones (F3a): lectura y escritura en Supabase (M41).
//  - Plantillas: las del sistema (solo lectura) y las de la empresa; estructura con insp_foto_plantilla y
//    insp_guardar_estructura (una transacción, una versión); duplicar con insp_duplicar_plantilla.
//  - Programaciones: cada una genera su próxima inspección "programada" con su evento del Calendario SSO
//    (event_type 'inspeccion'), igual que los simulacros (pe-simulacros-datos.ts).
//  - Inspecciones: iniciar (la base renueva la foto de la plantilla), responder ítem por ítem, fotos por ítem, firmar
//    (K6: PDF y firmas en <empresa>/inspecciones/<inspección>/firma/, carpeta congelada) y reabrir (RPC, solo admin).
//  - Hallazgos: automáticos (desde un "no cumple") o manuales; foto y evidencia de cierre en <empresa>/hallazgos/<id>/.
//  - Archivos en el bucket privado inspection-files: tipo, tamaño y cabecera revisados como las evidencias del plan
//    (pe-evidencias.ts); se abren con una URL firmada de 120 s.

import { supabase } from '../../supabaseClient';
import { nombreSeguro, revisarEvidencia, SEGUNDOS_URL_EVIDENCIA, TipoEvidencia } from '../pe/pe-evidencias';
import type { SeccionEditable } from './insp-logica';
import { estructuraParaGuardar, isoLocal, proximaFecha } from './insp-logica';
import {
  BUCKET_INSPECCIONES, FilaArchivo, FilaHallazgo, FilaInspeccion, FilaPlantilla, FilaProgramacion, FilaRespuesta, FirmaInspeccion,
  FotoPlantilla, MetodoFirma, RolFirma, TipoArchivo, ValorRespuesta,
} from './insp-tipos';

export const MENSAJE_SIN_PERMISO_INSP = 'No tienes permiso para este cambio: en Inspecciones el administrador y el gestor guardan, y solo el administrador borra.';

export function mensajeErrorInsp(error: { code?: string; message?: string } | null | undefined, accion: string): string {
  if (!error) return `No se pudo ${accion}.`;
  if (error.code === '42501') return error.message && /[áéíóúñ]/i.test(error.message) ? error.message : MENSAJE_SIN_PERMISO_INSP;
  // Mensajes de los triggers de M41 (en español): se muestran tal cual.
  if (error.code === '23514' && error.message && !/violates check constraint/.test(error.message)) return error.message;
  if (error.code === '23514') return `No se pudo ${accion}: un dato no cumple las reglas de la inspección.`;
  if (error.code === '23505') return `No se pudo ${accion}: ya existe un registro igual.`;
  if (error.code === '23503') return `No se pudo ${accion}: el registro relacionado ya no existe o todavía se usa.`;
  if (error.code === '42P01' || error.code === 'PGRST205' || error.code === '42883') {
    return `No se pudo ${accion}: falta actualizar la base de datos de Inspecciones. Avisa al soporte de StorSafe.`; // M41
  }
  return `No se pudo ${accion}: ${error.message ?? 'error desconocido'}.`;
}

const falla = (error: { code?: string; message?: string } | null, accion: string): never => {
  throw new Error(mensajeErrorInsp(error, accion));
};

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

// ── Plantillas ───────────────────────────────────────────────────────────────

const CAMPOS_PLANTILLA = 'id, company_id, code, name, description, category, is_library, anexo_items, default_frequency, frequency_source, version, active, source_template_id, updated_at';

/** Las del sistema (company_id null) y las de la empresa. */
export async function cargarPlantillas(companyId: string): Promise<FilaPlantilla[]> {
  const { data, error } = await supabase.from('insp_templates').select(CAMPOS_PLANTILLA)
    .or(`company_id.is.null,company_id.eq.${companyId}`).order('name');
  if (error) falla(error, 'leer las plantillas');
  return (data ?? []) as unknown as FilaPlantilla[];
}

const mapFoto = (f: any): FotoPlantilla => ({
  ...f, anexo_items: f?.anexo_items ?? [],
  sections: (f?.sections ?? []).map((s: any) => ({ ...s, items: (s.items ?? []).map((i: any) => ({ ...i, num_min: num(i.num_min), num_max: num(i.num_max) })) })),
});

export async function cargarEstructura(templateId: string): Promise<FotoPlantilla> {
  const { data, error } = await supabase.rpc('insp_foto_plantilla', { p_template: templateId });
  if (error) falla(error, 'leer la plantilla');
  if (!data) throw new Error('No se encontró la plantilla.');
  return mapFoto(data);
}

export interface DatosPlantilla { name: string; description: string | null; category: FilaPlantilla['category']; default_frequency: FilaPlantilla['default_frequency'] }

export async function crearPlantilla(companyId: string, d: DatosPlantilla, secciones: SeccionEditable[], usuarioId: string | null): Promise<string> {
  const { data, error } = await supabase.from('insp_templates').insert({
    company_id: companyId, name: d.name.trim(), description: d.description?.trim() || null, category: d.category,
    default_frequency: d.default_frequency, created_by: usuarioId, updated_by: usuarioId,
  }).select('id');
  if (error || !data?.length) falla(error ?? { code: '42501' }, 'crear la plantilla');
  const id = (data as { id: string }[])[0].id;
  await guardarEstructura(id, secciones);
  return id;
}

export async function actualizarPlantilla(id: string, d: DatosPlantilla, secciones: SeccionEditable[], usuarioId: string | null): Promise<void> {
  const { data, error } = await supabase.from('insp_templates').update({
    name: d.name.trim(), description: d.description?.trim() || null, category: d.category, default_frequency: d.default_frequency, updated_by: usuarioId,
  }).eq('id', id).select('id');
  if (error) falla(error, 'guardar la plantilla');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  await guardarEstructura(id, secciones);
}

async function guardarEstructura(id: string, secciones: SeccionEditable[]): Promise<number> {
  const { data, error } = await supabase.rpc('insp_guardar_estructura', { p_template: id, p_secciones: estructuraParaGuardar(secciones) });
  if (error) falla(error, 'guardar las secciones e ítems');
  return Number(data);
}

export async function duplicarPlantilla(origenId: string, companyId: string, nombre: string): Promise<string> {
  const { data, error } = await supabase.rpc('insp_duplicar_plantilla', { p_origen: origenId, p_empresa: companyId, p_nombre: nombre });
  if (error) falla(error, 'duplicar la plantilla');
  return data as string;
}

// Solo admin (RLS). Una plantilla con programaciones no se borra (FK): se desactiva.
export async function borrarPlantilla(id: string): Promise<void> {
  const { data, error } = await supabase.from('insp_templates').delete().eq('id', id).select('id');
  if (error) throw new Error(error.code === '23503' ? 'La plantilla tiene programaciones: desactívala en vez de borrarla.' : mensajeErrorInsp(error, 'borrar la plantilla'));
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
}

export async function activarPlantilla(id: string, activa: boolean): Promise<void> {
  const { data, error } = await supabase.from('insp_templates').update({ active: activa }).eq('id', id).select('id');
  if (error) falla(error, activa ? 'activar la plantilla' : 'desactivar la plantilla');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
}

// ── Inspecciones ─────────────────────────────────────────────────────────────

export function mapInspeccion(r: any): FilaInspeccion {
  return { ...r, template_snapshot: mapFoto(r.template_snapshot ?? {}), signatures: r.signatures ?? [], reopen_history: r.reopen_history ?? [] } as FilaInspeccion;
}

export async function cargarInspecciones(companyId: string): Promise<FilaInspeccion[]> {
  const { data, error } = await supabase.from('insp_inspections').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
  if (error) falla(error, 'leer las inspecciones');
  return (data ?? []).map(mapInspeccion);
}

export interface NuevaInspeccion {
  templateId: string;
  templateName: string;
  siteId: string | null;
  area: string | null;
  fecha: string;
  responsableId: string | null;
  responsableCargo: string | null;
  scheduleId?: string | null;
  ahora: boolean;            // true = se hace ya (en curso, fecha realizada hoy); false = programada
}

const tituloEvento = (nombre: string) => `Inspección: ${nombre}`.slice(0, 200);

async function crearEvento(companyId: string, inspeccion: FilaInspeccion, usuarioId: string | null): Promise<string | null> {
  const { data, error } = await supabase.from('sso_calendar_events').insert({
    company_id: companyId, title: tituloEvento(inspeccion.template_name), event_type: 'inspeccion', related_id: inspeccion.id,
    related_table: 'insp_inspections', due_date: inspeccion.scheduled_date, status: 'pendiente', assigned_to: null,
    notes: inspeccion.area, completed_date: null, created_by: usuarioId,
  }).select('id');
  if (error || !data?.length) {
    console.error('crearEvento inspección', error);
    return null;
  }
  const id = (data as { id: string }[])[0].id;
  const { error: e2 } = await supabase.from('insp_inspections').update({ calendar_event_id: id }).eq('id', inspeccion.id);
  if (e2) console.error('enlace al calendario', e2);
  return id;
}

/** Crea la inspección; si es programada, también su evento del Calendario SSO. */
export async function crearInspeccion(companyId: string, n: NuevaInspeccion, usuarioId: string | null): Promise<{ fila: FilaInspeccion; avisoCalendario: string | null }> {
  const { data, error } = await supabase.from('insp_inspections').insert({
    company_id: companyId, template_id: n.templateId, site_id: n.siteId || null, area: n.area?.trim() || null,
    status: n.ahora ? 'en_curso' : 'programada', scheduled_date: n.ahora ? null : n.fecha, performed_date: n.ahora ? n.fecha : null,
    responsible_worker_id: n.responsableId || null, responsible_role: n.responsableCargo?.trim() || null, schedule_id: n.scheduleId ?? null,
    created_by: usuarioId, updated_by: usuarioId,
  }).select('*');
  if (error || !data?.length) falla(error ?? { code: '42501' }, n.ahora ? 'crear la inspección' : 'programar la inspección');
  const fila = mapInspeccion((data as any[])[0]);
  if (n.ahora) return { fila, avisoCalendario: null };
  const ev = await crearEvento(companyId, fila, usuarioId);
  return ev ? { fila: { ...fila, calendar_event_id: ev }, avisoCalendario: null }
    : { fila, avisoCalendario: 'La inspección quedó programada, pero no se pudo crear el evento del Calendario SSO.' };
}

/** Iniciar: pasa a en curso con la fecha de hoy. La base toma la foto de la plantilla en ese momento (M41). */
export async function iniciarInspeccion(i: Pick<FilaInspeccion, 'id'>, usuarioId: string | null, hoy: Date = new Date()): Promise<FilaInspeccion> {
  const { data, error } = await supabase.from('insp_inspections').update({ status: 'en_curso', performed_date: isoLocal(hoy), updated_by: usuarioId })
    .eq('id', i.id).select('*');
  if (error) falla(error, 'iniciar la inspección');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  return mapInspeccion((data as any[])[0]);
}

export async function guardarDatosInspeccion(id: string, d: Partial<Pick<FilaInspeccion, 'area' | 'observations' | 'performed_date' | 'responsible_worker_id' | 'responsible_role'>>, usuarioId: string | null): Promise<FilaInspeccion> {
  const { data, error } = await supabase.from('insp_inspections').update({ ...d, updated_by: usuarioId }).eq('id', id).select('*');
  if (error) falla(error, 'guardar la inspección');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  return mapInspeccion((data as any[])[0]);
}

export async function cancelarInspeccion(i: Pick<FilaInspeccion, 'id' | 'calendar_event_id'>, usuarioId: string | null): Promise<void> {
  const { data, error } = await supabase.from('insp_inspections').update({ status: 'cancelada', updated_by: usuarioId }).eq('id', i.id).select('id');
  if (error) falla(error, 'cancelar la inspección');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  if (i.calendar_event_id) {
    const { error: e2 } = await supabase.from('sso_calendar_events').update({ status: 'cancelado' }).eq('id', i.calendar_event_id);
    if (e2) console.error('cancelarInspeccion: calendario', e2);
  }
}

// ── Borrar (F3a2): solo el admin (RLS). Una firmada no se borra: primero se reabre con un motivo (trigger de M41). ──

export const MENSAJE_BORRAR_FIRMADA = 'La inspección está firmada: reábrela con un motivo antes de borrarla.';

/** Rutas que se quitan de Storage al borrar: nunca las de la carpeta firma/ (congelada por diseño). */
export const rutasBorrables = (rutas: (string | null | undefined)[]): string[] =>
  rutas.filter((r): r is string => !!r && !/\/firma\//.test(r));

async function quitarDelBucket(rutas: (string | null | undefined)[], contexto: string): Promise<void> {
  const r = rutasBorrables(rutas);
  if (!r.length) return;
  const { error } = await supabase.storage.from(BUCKET_INSPECCIONES).remove(r);
  if (error) console.error(`${contexto}: storage`, error);
}

async function rutasDeHallazgos(ids: string[]): Promise<string[]> {
  if (!ids.length) return [];
  const { data, error } = await supabase.from('insp_files').select('storage_path').in('finding_id', ids);
  if (error) falla(error, 'leer los archivos de los hallazgos');
  return (data ?? []).map((f: { storage_path: string }) => f.storage_path);
}

/**
 * Borra una inspección no firmada con sus respuestas, fotos por ítem y hallazgos (cascada en la base), sus archivos
 * de Storage (salvo firma/) y su evento del Calendario SSO. Primero la fila: si la base la frena, no se toca nada más.
 */
export async function borrarInspeccion(i: Pick<FilaInspeccion, 'id' | 'signed_at' | 'calendar_event_id'>): Promise<void> {
  if (i.signed_at) throw new Error(MENSAJE_BORRAR_FIRMADA);
  const [{ data: fotos, error: e1 }, { data: hall, error: e2 }] = await Promise.all([
    supabase.from('insp_files').select('storage_path').eq('inspection_id', i.id),
    supabase.from('insp_findings').select('id').eq('inspection_id', i.id),
  ]);
  if (e1 || e2) falla((e1 ?? e2)!, 'leer lo que tiene la inspección');
  const rutas = [...(fotos ?? []).map((f: { storage_path: string }) => f.storage_path),
    ...await rutasDeHallazgos((hall ?? []).map((h: { id: string }) => h.id))];
  const { data, error } = await supabase.from('insp_inspections').delete().eq('id', i.id).select('id');
  if (error) throw new Error(/firmada/i.test(error.message ?? '') ? MENSAJE_BORRAR_FIRMADA : mensajeErrorInsp(error, 'borrar la inspección'));
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  await quitarDelBucket(rutas, 'borrarInspeccion');
  if (i.calendar_event_id) {
    const { error: e3 } = await supabase.from('sso_calendar_events').delete().eq('id', i.calendar_event_id);
    if (e3) console.error('borrarInspeccion: calendario', e3);
  }
}

/**
 * Borra una programación. Con "conPendientes", antes borra las inspecciones que generó y siguen programadas (con su
 * evento); las demás (en curso, completadas o canceladas) quedan, sin programación.
 */
export async function borrarProgramacion(p: Pick<FilaProgramacion, 'id'>, inspecciones: FilaInspeccion[], conPendientes: boolean): Promise<number> {
  const pendientes = conPendientes ? inspecciones.filter(i => i.schedule_id === p.id && i.status === 'programada' && !i.signed_at) : [];
  for (const i of pendientes) await borrarInspeccion(i);
  const { data, error } = await supabase.from('insp_schedules').delete().eq('id', p.id).select('id');
  if (error) falla(error, 'borrar la programación');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  return pendientes.length;
}

// ── Respuestas ───────────────────────────────────────────────────────────────

export async function cargarRespuestas(inspeccionId: string): Promise<FilaRespuesta[]> {
  const { data, error } = await supabase.from('insp_answers').select('id, inspection_id, item_key, value, number_value, text_value, note').eq('inspection_id', inspeccionId);
  if (error) falla(error, 'leer las respuestas');
  return (data ?? []).map((r: any) => ({ ...r, number_value: num(r.number_value) })) as FilaRespuesta[];
}

export interface CambioRespuesta { value?: ValorRespuesta | null; number_value?: number | null; text_value?: string | null; note?: string | null }

export async function guardarRespuesta(i: Pick<FilaInspeccion, 'id' | 'company_id'>, itemKey: string, actual: FilaRespuesta | undefined, cambio: CambioRespuesta,
  usuarioId: string | null): Promise<FilaRespuesta> {
  const fila = {
    company_id: i.company_id, inspection_id: i.id, item_key: itemKey, value: actual?.value ?? null, number_value: actual?.number_value ?? null,
    text_value: actual?.text_value ?? null, note: actual?.note ?? null, ...cambio, answered_by: usuarioId, answered_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('insp_answers').upsert(fila, { onConflict: 'inspection_id,item_key' })
    .select('id, inspection_id, item_key, value, number_value, text_value, note');
  if (error) falla(error, 'guardar la respuesta');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  const r = (data as any[])[0];
  return { ...r, number_value: num(r.number_value) } as FilaRespuesta;
}

// ── Archivos ─────────────────────────────────────────────────────────────────

export async function cargarArchivos(companyId: string, filtro: { inspeccionId?: string; hallazgos?: string[] }): Promise<FilaArchivo[]> {
  let q = supabase.from('insp_files').select('*').eq('company_id', companyId);
  if (filtro.inspeccionId) q = q.eq('inspection_id', filtro.inspeccionId);
  if (filtro.hallazgos) {
    if (!filtro.hallazgos.length) return [];
    q = q.in('finding_id', filtro.hallazgos);
  }
  const { data, error } = await q.order('created_at');
  if (error) falla(error, 'leer los archivos');
  return (data ?? []).map((r: any) => ({ ...r, size_bytes: Number(r.size_bytes) })) as FilaArchivo[];
}

export const rutaFotoItem = (companyId: string, inspeccionId: string, archivo: string, marca = Date.now()) =>
  `${companyId}/inspecciones/${inspeccionId}/fotos/${marca}-${archivo}`;
export const rutaHallazgo = (companyId: string, hallazgoId: string, archivo: string, marca = Date.now()) =>
  `${companyId}/hallazgos/${hallazgoId}/${marca}-${archivo}`;
export const rutaFirma = (companyId: string, inspeccionId: string, archivo: string, marca = Date.now()) =>
  `${companyId}/inspecciones/${inspeccionId}/firma/${marca}-${archivo}`;

async function subirAlBucket(ruta: string, cuerpo: Blob, tipo: string, accion: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET_INSPECCIONES).upload(ruta, cuerpo, { contentType: tipo, upsert: false });
  if (error) {
    const sinPermiso = /row-level security|unauthorized|403/i.test(error.message ?? '');
    throw new Error(sinPermiso ? MENSAJE_SIN_PERMISO_INSP : `No se pudo ${accion}: ${error.message}.`);
  }
}

/** Revisa tipo, tamaño y cabecera; sube el archivo y registra la fila. Si la fila falla, se quita el archivo. */
export async function subirArchivo(companyId: string, destino: { kind: 'foto_item'; inspeccionId: string; itemKey: string } | { kind: Exclude<TipoArchivo, 'foto_item'>; hallazgoId: string },
  archivo: File, usuarioId: string | null): Promise<FilaArchivo> {
  const r = await revisarEvidencia(archivo);
  if ('error' in r) throw new Error(r.error);
  const nombre = nombreSeguro(archivo.name, r.tipo);
  const ruta = destino.kind === 'foto_item' ? rutaFotoItem(companyId, destino.inspeccionId, nombre) : rutaHallazgo(companyId, destino.hallazgoId, nombre);
  const cuerpo = archivo.type === r.tipo ? archivo : new File([archivo], nombre, { type: r.tipo });
  await subirAlBucket(ruta, cuerpo, r.tipo, 'subir el archivo');
  const { data, error } = await supabase.from('insp_files').insert({
    company_id: companyId, kind: destino.kind, storage_path: ruta, file_name: archivo.name.slice(0, 200) || nombre, mime_type: r.tipo,
    size_bytes: archivo.size, uploaded_by: usuarioId,
    ...(destino.kind === 'foto_item' ? { inspection_id: destino.inspeccionId, item_key: destino.itemKey } : { finding_id: destino.hallazgoId }),
  }).select('*');
  if (error || !data?.length) {
    await supabase.storage.from(BUCKET_INSPECCIONES).remove([ruta]).catch(() => {});
    falla(error ?? { code: '42501' }, 'registrar el archivo');
  }
  const f = (data as any[])[0];
  return { ...f, size_bytes: Number(f.size_bytes) } as FilaArchivo;
}

/** Primero la fila (si la RLS o el trigger la frenan, el archivo queda intacto), después el archivo. */
export async function borrarArchivo(a: Pick<FilaArchivo, 'id' | 'storage_path'>): Promise<void> {
  const { data, error } = await supabase.from('insp_files').delete().eq('id', a.id).select('id');
  if (error) falla(error, 'borrar el archivo');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  const { error: e2 } = await supabase.storage.from(BUCKET_INSPECCIONES).remove([a.storage_path]);
  if (e2) console.error('borrarArchivo: storage', e2);
}

export async function urlArchivo(ruta: string, descarga?: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_INSPECCIONES).createSignedUrl(ruta, SEGUNDOS_URL_EVIDENCIA, descarga ? { download: descarga } : undefined);
  if (error || !data?.signedUrl) throw new Error(`No se pudo abrir el archivo: ${error?.message ?? 'sin enlace'}.`);
  return data.signedUrl;
}

/** Miniatura para el PDF: la foto como data URL (los PDF adjuntos no se incrustan). */
export async function dataUrlArchivo(a: Pick<FilaArchivo, 'storage_path' | 'mime_type'>): Promise<string | null> {
  if (a.mime_type === 'application/pdf') return null;
  const { data, error } = await supabase.storage.from(BUCKET_INSPECCIONES).download(a.storage_path);
  if (error || !data) return null;
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result as string);
    lector.onerror = () => resolve(null);
    lector.readAsDataURL(data);
  });
}

// ── Hallazgos ────────────────────────────────────────────────────────────────

export async function cargarHallazgos(companyId: string): Promise<FilaHallazgo[]> {
  const { data, error } = await supabase.from('insp_findings').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
  if (error) falla(error, 'leer los hallazgos');
  return (data ?? []) as FilaHallazgo[];
}

export type HallazgoEditable = Pick<FilaHallazgo, 'description' | 'risk_level' | 'corrective_action' | 'responsible_worker_id' | 'responsible_role' | 'due_date'>;

export async function crearHallazgos(companyId: string, filas: (HallazgoEditable & { site_id: string | null; inspection_id: string | null; item_key: string | null;
  origin: FilaHallazgo['origin'] })[], usuarioId: string | null): Promise<FilaHallazgo[]> {
  if (!filas.length) return [];
  const { data, error } = await supabase.from('insp_findings')
    .insert(filas.map(f => ({ ...f, company_id: companyId, created_by: usuarioId, updated_by: usuarioId }))).select('*');
  if (error) falla(error, 'crear los hallazgos');
  return (data ?? []) as FilaHallazgo[];
}

export async function guardarHallazgo(id: string, d: Partial<HallazgoEditable> & { status?: FilaHallazgo['status']; closure_note?: string | null }, usuarioId: string | null): Promise<FilaHallazgo> {
  const { data, error } = await supabase.from('insp_findings').update({ ...d, updated_by: usuarioId }).eq('id', id).select('*');
  if (error) falla(error, d.status === 'cerrado' ? 'cerrar el hallazgo' : 'guardar el hallazgo');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  return (data as FilaHallazgo[])[0];
}

/** Borra el hallazgo (sus filas de archivos caen en cascada) y después sus fotos y evidencias de Storage (F3a2). */
export async function borrarHallazgo(id: string): Promise<void> {
  const rutas = await rutasDeHallazgos([id]);
  const { data, error } = await supabase.from('insp_findings').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar el hallazgo');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
  await quitarDelBucket(rutas, 'borrarHallazgo');
}

// ── Programaciones ───────────────────────────────────────────────────────────

export async function cargarProgramaciones(companyId: string): Promise<FilaProgramacion[]> {
  const { data, error } = await supabase.from('insp_schedules').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
  if (error) falla(error, 'leer las programaciones');
  return (data ?? []) as FilaProgramacion[];
}

// site_id null = la Matriz (la pone la base, M40): con una sola sede activa no hay selector.
export type ProgramacionEditable = Pick<FilaProgramacion, 'template_id' | 'area' | 'frequency' | 'start_date' | 'end_date'
  | 'responsible_worker_id' | 'responsible_role' | 'notes'> & { site_id: string | null };

/** Guarda la programación y genera su primera inspección programada (con su evento del Calendario SSO). */
export async function crearProgramacion(companyId: string, p: ProgramacionEditable, templateName: string, usuarioId: string | null)
  : Promise<{ programacion: FilaProgramacion; inspeccion: FilaInspeccion; avisoCalendario: string | null }> {
  const { data, error } = await supabase.from('insp_schedules').insert({
    company_id: companyId, template_id: p.template_id, site_id: p.site_id || null, area: p.area?.trim() || null, frequency: p.frequency,
    start_date: p.start_date, end_date: p.frequency === 'unica' ? null : p.end_date || null, responsible_worker_id: p.responsible_worker_id || null,
    responsible_role: p.responsible_role?.trim() || null, notes: p.notes?.trim() || null, created_by: usuarioId, updated_by: usuarioId,
  }).select('*');
  if (error || !data?.length) falla(error ?? { code: '42501' }, 'guardar la programación');
  const programacion = (data as FilaProgramacion[])[0];
  const r = await crearInspeccion(companyId, {
    templateId: programacion.template_id, templateName, siteId: programacion.site_id, area: programacion.area, fecha: programacion.start_date,
    responsableId: programacion.responsible_worker_id, responsableCargo: programacion.responsible_role, scheduleId: programacion.id, ahora: false,
  }, usuarioId);
  return { programacion, inspeccion: r.fila, avisoCalendario: r.avisoCalendario };
}

/**
 * Después de completar o cancelar una inspección de una programación: genera la siguiente (si la programación sigue
 * activa, no es única y no pasó su fecha de fin) y no duplica una que ya esté pendiente.
 */
export async function generarSiguiente(companyId: string, p: FilaProgramacion, inspecciones: Pick<FilaInspeccion, 'schedule_id' | 'status' | 'scheduled_date' | 'performed_date'>[],
  templateName: string, usuarioId: string | null): Promise<FilaInspeccion | null> {
  if (!p.active) return null;
  const propias = inspecciones.filter(i => i.schedule_id === p.id);
  if (propias.some(i => i.status === 'programada' || i.status === 'en_curso')) return null;
  const ultima = propias.map(i => i.scheduled_date ?? i.performed_date ?? '').filter(Boolean).sort().pop() ?? null;
  const fecha = proximaFecha(p, ultima);
  if (!fecha) return null;
  const r = await crearInspeccion(companyId, {
    templateId: p.template_id, templateName, siteId: p.site_id, area: p.area, fecha, responsableId: p.responsible_worker_id,
    responsableCargo: p.responsible_role, scheduleId: p.id, ahora: false,
  }, usuarioId);
  return r.fila;
}

export async function pausarProgramacion(id: string, activa: boolean): Promise<void> {
  const { data, error } = await supabase.from('insp_schedules').update({ active: activa }).eq('id', id).select('id');
  if (error) falla(error, activa ? 'reanudar la programación' : 'pausar la programación');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
}

// ── Firma (K6) y reapertura ──────────────────────────────────────────────────

export interface FirmaParaSubir {
  rol: RolFirma; nombre: string; cedula: string; firmadoEn: string; hash: string;
  png?: Blob | null;      // trazo (firma en pantalla)
  archivo?: File | null;  // papel escaneado o PDF FirmaEC
}

/**
 * Sube trazos o archivos firmados y el PDF a la carpeta firma/ (congelada) y marca la inspección como completada y
 * firmada. Desde aquí el trigger de M41 la bloquea. El evento del calendario se cierra.
 */
export async function firmarInspeccion(i: FilaInspeccion, metodo: MetodoFirma, firmas: FirmaParaSubir[], pdf: Blob, hash: string): Promise<FilaInspeccion> {
  const marca = Date.now();
  const subidos: string[] = [];
  try {
    const guardadas: FirmaInspeccion[] = [];
    for (const f of firmas) {
      const g: FirmaInspeccion = { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim(), firmado_en: f.firmadoEn, hash: f.hash, metodo };
      if (f.png) {
        const ruta = rutaFirma(i.company_id, i.id, `firma_${f.rol}.png`, marca);
        await subirAlBucket(ruta, f.png, 'image/png', 'subir la firma'); subidos.push(ruta); g.firma_path = ruta;
      }
      if (f.archivo) {
        const r = await revisarEvidencia(f.archivo);
        if ('error' in r) throw new Error(r.error);
        const ruta = rutaFirma(i.company_id, i.id, `firmado_${f.rol}-${nombreSeguro(f.archivo.name, r.tipo as TipoEvidencia)}`, marca);
        await subirAlBucket(ruta, f.archivo.type === r.tipo ? f.archivo : new File([f.archivo], 'firmado', { type: r.tipo }), r.tipo, 'subir el documento firmado');
        subidos.push(ruta); g.archivo_path = ruta;
      }
      guardadas.push(g);
    }
    const pdfPath = rutaFirma(i.company_id, i.id, 'inspeccion.pdf', marca);
    await subirAlBucket(pdfPath, pdf, 'application/pdf', 'subir el PDF de la inspección'); subidos.push(pdfPath);
    const { data, error } = await supabase.from('insp_inspections').update({
      status: 'completada', signed_at: new Date().toISOString(), sign_method: metodo, signatures: guardadas, pdf_path: pdfPath, sign_hash: hash,
    }).eq('id', i.id).select('*');
    if (error) falla(error, 'firmar la inspección');
    if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO_INSP);
    if (i.calendar_event_id) {
      const { error: e2 } = await supabase.from('sso_calendar_events').update({ status: 'completado', completed_date: i.performed_date }).eq('id', i.calendar_event_id);
      if (e2) console.error('firmarInspeccion: calendario', e2);
    }
    return mapInspeccion((data as any[])[0]);
  } catch (e) {
    // La carpeta firma/ no la borra nadie autenticado (M41): si algo falló, los archivos quedan sin fila.
    if (subidos.length) console.error('firmarInspeccion: archivos subidos sin firmar', subidos.length);
    throw e;
  }
}

export async function reabrirInspeccion(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('reabrir_inspeccion', { p_inspeccion: id, p_motivo: motivo });
  if (error) throw new Error(error.code === '42501' ? 'Solo el administrador de la empresa puede reabrir una inspección firmada.' : mensajeErrorInsp(error, 'reabrir la inspección'));
}

// ── Trabajadores (responsables) ──────────────────────────────────────────────

/** Activos del inventario (M50) para la columna "Activo". Sin la tabla o sin poder leerla: lista vacía (la columna muestra "—"). */
export async function cargarActivosMin(companyId: string): Promise<{ id: string; code: string; name: string }[]> {
  try {
    const { data, error } = await supabase.from('assets').select('id, code, name').eq('company_id', companyId);
    return error ? [] : ((data ?? []) as { id: string; code: string; name: string }[]);
  } catch {
    return [];
  }
}

export async function cargarTrabajadores(companyId: string): Promise<{ id: string; full_name: string }[]> {
  const { data, error } = await supabase.from('workers').select('id, full_name').eq('company_id', companyId).eq('status', 'activo').order('full_name');
  if (error) falla(error, 'leer los trabajadores');
  return (data ?? []) as { id: string; full_name: string }[];
}
